/** Normalize list payloads from various API response shapes. */
export const extractList = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.geofences)) return payload.geofences;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const extractRecord = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  if (payload?.geofence) return payload.geofence;
  return payload;
};

export const normalizeGeofence = (item = {}) => {
  const centerLat = Number.parseFloat(
    item.center_lat ?? item.centerLat ?? item.latitude ?? item.lat ?? item.center?.lat
  );
  const centerLng = Number.parseFloat(
    item.center_lng ??
      item.centerLng ??
      item.longitude ??
      item.lng ??
      item.lon ??
      item.center?.lng
  );
  const radius = Number.parseFloat(
    item.radius_meters ?? item.radiusMeters ?? item.radius_m ?? item.radius ?? 500
  );

  return {
    id: item.id ?? item._id ?? item.geofence_id ?? null,
    name: item.name || item.title || item.zone_name || 'Unnamed zone',
    vehicleId: item.vehicle_id ?? item.vehicleId ?? null,
    deviceId: item.device_id ?? item.deviceId ?? null,
    vehicleName: item.vehicle_name ?? item.vehicleName ?? '',
    vehicleNumber: item.vehicle_number ?? item.vehicleNumber ?? '',
    type: (item.type || item.shape || 'circle').toLowerCase(),
    centerLat: Number.isFinite(centerLat) ? centerLat : null,
    centerLng: Number.isFinite(centerLng) ? centerLng : null,
    radiusMeters: Number.isFinite(radius) && radius > 0 ? radius : 500,
    alertOnEnter: Boolean(
      item.alert_on_enter ?? item.alertOnEnter ?? item.notify_on_enter ?? true
    ),
    alertOnExit: Boolean(
      item.alert_on_exit ?? item.alertOnExit ?? item.notify_on_exit ?? true
    ),
    enabled: Boolean(item.enabled ?? item.is_active ?? item.active ?? true),
    raw: item,
  };
};

export const buildGeofencePayload = ({
  name,
  vehicleId,
  deviceId,
  centerLat,
  centerLng,
  radiusMeters,
  alertOnEnter,
  alertOnExit,
  enabled,
}) => ({
  name: String(name || '').trim(),
  vehicle_id: vehicleId || undefined,
  device_id: deviceId || undefined,
  type: 'circle',
  center_lat: Number(centerLat),
  center_lng: Number(centerLng),
  latitude: Number(centerLat),
  longitude: Number(centerLng),
  radius_meters: Number(radiusMeters),
  radius: Number(radiusMeters),
  alert_on_enter: Boolean(alertOnEnter),
  alert_on_exit: Boolean(alertOnExit),
  enabled: Boolean(enabled),
});

/**
 * Try several customer geofence endpoints so the UI works across backend variants.
 * Returns { data, endpoint } for the first successful call.
 */
export async function fetchCustomerGeofences(get, { vehicleId, deviceId } = {}) {
  const candidates = [];

  if (vehicleId) {
    candidates.push(`/customer/my-vehicles/${vehicleId}/geofences`);
    candidates.push(`/customer/geofences?vehicle_id=${encodeURIComponent(vehicleId)}`);
  }
  if (deviceId) {
    candidates.push(`/customer/geofences?device_id=${encodeURIComponent(deviceId)}`);
  }
  candidates.push('/customer/geofences');
  candidates.push('/customer/my-vehicles/geofences');
  candidates.push('/customer/geofence-settings');

  let lastError;
  for (const endpoint of candidates) {
    try {
      const data = await get(endpoint);
      return { data, endpoint };
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && status !== 404) throw error;
    }
  }
  throw lastError || new Error('Unable to load geofences.');
}

export async function createCustomerGeofence(post, payload) {
  const vehicleId = payload.vehicle_id;
  const candidates = [
    '/customer/geofences',
    vehicleId ? `/customer/my-vehicles/${vehicleId}/geofences` : null,
    '/customer/geofence-settings',
  ].filter(Boolean);

  let lastError;
  for (const endpoint of candidates) {
    try {
      return await post(endpoint, payload);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && ![404, 405].includes(status)) throw error;
    }
  }
  throw lastError || new Error('Unable to create geofence.');
}

export async function updateCustomerGeofence(put, patch, geofenceId, payload) {
  const candidates = [
    { method: 'put', url: `/customer/geofences/${geofenceId}` },
    { method: 'patch', url: `/customer/geofences/${geofenceId}` },
    payload.vehicle_id
      ? {
          method: 'put',
          url: `/customer/my-vehicles/${payload.vehicle_id}/geofences/${geofenceId}`,
        }
      : null,
  ].filter(Boolean);

  let lastError;
  for (const { method, url } of candidates) {
    try {
      const caller = method === 'put' ? put : patch;
      return await caller(url, payload);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && ![404, 405].includes(status)) throw error;
    }
  }
  throw lastError || new Error('Unable to update geofence.');
}

export async function deleteCustomerGeofence(del, geofenceId, { vehicleId } = {}) {
  const candidates = [
    `/customer/geofences/${geofenceId}`,
    vehicleId ? `/customer/my-vehicles/${vehicleId}/geofences/${geofenceId}` : null,
  ].filter(Boolean);

  let lastError;
  for (const endpoint of candidates) {
    try {
      return await del(endpoint);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && ![404, 405].includes(status)) throw error;
    }
  }
  throw lastError || new Error('Unable to delete geofence.');
}

export const formatRadius = (meters) => {
  const m = Number(meters);
  if (!Number.isFinite(m)) return '—';
  if (m >= 1000) return `${(m / 1000).toFixed(m % 1000 === 0 ? 0 : 1)} km`;
  return `${Math.round(m)} m`;
};
